import { useEffect, useRef, useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import EditableText from '../../../components/cms/EditableText';
import EditableImage from '../../../components/cms/EditableImage';

const isNarrowViewport = () => window.innerWidth < 768;

declare global {
  interface Window {
    Calendly?: {
      initInlineWidget: (args: {
        url: string;
        parentElement: HTMLElement;
        resize?: boolean;
      }) => void;
    };
  }
}

interface CalendlySectionProps {
  calendlyUrl: string;
}

function withCalendlyParams(url: string): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}hide_gdpr_banner=1`;
}

export default function CalendlySection({ calendlyUrl }: CalendlySectionProps) {
  const { ref, isVisible } = useScrollAnimation();
  const [widgetHeight, setWidgetHeight] = useState(() => (isNarrowViewport() ? 700 : 1050));
  const widgetContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleResize = () => {
      setWidgetHeight((prev) => {
        const mobile = isNarrowViewport();
        if (mobile && prev > 750) return 700;
        if (!mobile && prev < 900) return 1050;
        return prev;
      });
    };
    window.addEventListener('resize', handleResize);

    const initWidget = () => {
      if (!widgetContainerRef.current || !window.Calendly?.initInlineWidget) return;
      widgetContainerRef.current.innerHTML = '';
      window.Calendly.initInlineWidget({
        url: withCalendlyParams(calendlyUrl),
        parentElement: widgetContainerRef.current,
        resize: true,
      });
    };

    const existingScript = document.querySelector(
      'script[src="https://assets.calendly.com/assets/external/widget.js"]'
    ) as HTMLScriptElement | null;

    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.onload = initWidget;
      document.head.appendChild(script);
    } else {
      initWidget();
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [calendlyUrl]);

  return (
    <section id="agendar" className="relative overflow-hidden">
      <div className="absolute inset-0 z-0">
        <EditableImage
          imageKey="contact_bg"
          alt=""
          wrapperClassName="absolute inset-0 w-full h-full"
          className="w-full h-full object-cover object-center min-h-full"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70 z-[1]" />

      <div
        ref={ref}
        className={`relative z-10 max-w-5xl mx-auto px-6 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="text-center pt-24 mb-6">
          <div className="inline-block mb-4">
            <EditableText
              section="calendly"
              field="badge"
              tag="span"
              className="text-xs font-bold uppercase tracking-[0.3em] text-[#8B9D83]"
            />
          </div>
          <EditableText
            section="calendly"
            field="title"
            tag="h2"
            className="font-serif text-4xl md:text-5xl font-bold text-white leading-tight"
          />
          <EditableText
            section="calendly"
            field="subtitle"
            tag="p"
            multiline
            className="text-stone-300 text-base mt-4 max-w-lg mx-auto leading-relaxed"
          />
        </div>

        <div className="bg-transparent rounded-3xl overflow-hidden pb-0" style={{ minHeight: widgetHeight }}>
          <div ref={widgetContainerRef} className="w-full" style={{ minWidth: 320, height: widgetHeight }} />
        </div>
        <div className="pb-16" />
      </div>
      <style>{`
        @media (min-width: 1024px) {
          #agendar [data-container="booking-container"] {
            width: 50% !important;
            margin: 0 auto !important;
          }
        }
      `}</style>
    </section>
  );
}
